import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import GridTable from '../../components/GridTable';
import { MODULES } from '../../utils/constants';
import SendNotificationDialog from './SendNotificationDialog';
import { getColumns } from './constants';
import { useNotificationStore } from '../../services/notifications';
import { doGET } from '../../utils/HttpUtils';
import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { NotificationQueryParams, NotificationType } from '../../types/notifications';
import { NOTIFICATION_TYPES } from './constants';

const Notifications = () => {
  const {
    data,
    totalPages,
    currentPage,
    total,
    limit,
    isLoading,
    onPageChange,
    setFilters,
    filters,
  } = useNotificationStore();

  const [typeFilter, setTypeFilter] = useState<NotificationType | ''>(filters.type ?? '');
  const [isReadFilter, setIsReadFilter] = useState<string>(filters.isRead === undefined ? '' : String(filters.isRead));
  const [userNamesMap, setUserNamesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setFilters({ page: 1, limit });
  }, []);

  useEffect(() => {
    if (!data?.length) {
      setUserNamesMap({});
      return;
    }
    const userIds = Array.from(new Set(data.map((row: any) => row.userId).filter(Boolean))) as string[];
    if (userIds.length === 0) {
      setUserNamesMap({});
      return;
    }
    let cancelled = false;
    Promise.allSettled(userIds.map((id) => doGET(`/users/${id}`))).then((results) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      results.forEach((result, i) => {
        const userId = userIds[i];
        if (result.status === 'fulfilled' && result.value?.data) {
          const raw = result.value.data;
          const u = raw?.data?.user ?? raw?.user ?? raw?.data ?? raw;
          if (u && userId) {
            map[userId] = u.name || u.email || u.phone || userId;
          }
        }
      });
      setUserNamesMap((prev) => ({ ...prev, ...map }));
    });
    return () => {
      cancelled = true;
    };
  }, [data]);

  return (
    <Layout appBarTitle="Notifications">
      <Layout.Header component={SendNotificationDialog} props={{ module: MODULES.NOTIFICATION }} />
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter((e.target.value as NotificationType) || '');
                setFilters({
                  ...filters,
                  type: (e.target.value as NotificationType) || undefined,
                  page: 1,
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              {NOTIFICATION_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Read</InputLabel>
            <Select
              label="Read"
              value={isReadFilter}
              onChange={(e) => {
                setIsReadFilter(e.target.value);
                setFilters({
                  ...filters,
                  isRead: e.target.value === '' ? undefined : e.target.value === 'true',
                  page: 1,
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Read</MenuItem>
              <MenuItem value="false">Unread</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
        </Stack>
      </Box>
      <Layout.Body
        component={GridTable}
        props={{
          data,
          columns: getColumns(currentPage, limit, userNamesMap),
          currentPage,
          totalPages,
          total,
          loading: isLoading,
          onPageChange,
          module: MODULES.NOTIFICATION,
          rows: limit,
          hideAction: true,
        }}
      />
    </Layout>
  );
};

export default Notifications;
